import clsx from 'clsx';
import { publicListingPath } from '@mts/shared/publicListingUrls';

export const LISTING_CARD_VARIANTS = ['text', 'chip', 'row', 'compact', 'content', 'directory', 'hero', 'directoryRow'];
export const LISTING_CARD_CONTEXTS = ['public', 'article', 'directory', 'homepage', 'admin', 'owner', 'claim'];
export const LISTING_CARD_DENSITIES = ['compact', 'comfortable'];
export const LISTING_CARD_TIERS = ['basic', 'featured', 'spotlight'];
export const LISTING_CARD_MEDIA_MODES = ['none', 'logo', 'image', 'background'];
export const LISTING_CARD_MEDIA_ASPECT_RATIOS = ['square', 'wide', 'portrait'];

export const DEFAULT_SHOW = {
  favorite: false,
  logo: true,
  image: true,
  category: true,
  description: true,
  rating: true,
  reviews: true,
  address: true,
  phone: false,
  website: false,
  hours: false,
  offer: true,
  badges: true,
};

const VARIANT_DEFAULTS = {
  text: {
    media: { mode: 'none' },
    show: { logo: false, image: false, description: false, rating: false, reviews: false, address: false, offer: false, badges: false },
  },
  chip: {
    media: { mode: 'logo', aspectRatio: 'square' },
    show: { image: false, category: false, description: false, rating: false, reviews: false, address: false, offer: false },
  },
  row: {
    media: { mode: 'logo', aspectRatio: 'square', overlay: false },
    show: {
      favorite: false,
      logo: true,
      image: false,
      category: true,
      description: false,
      rating: false,
      reviews: false,
      address: false,
      phone: false,
      website: false,
      hours: false,
      offer: false,
      badges: false,
    },
    behavior: 'clickable',
  },
  compact: {
    media: { mode: 'none', aspectRatio: 'square' },
    show: {
      favorite: false,
      logo: false,
      image: false,
      category: true,
      description: false,
      rating: false,
      reviews: false,
      address: false,
      phone: false,
      website: false,
      hours: false,
      offer: false,
      badges: false,
    },
  },
  content: {
    media: { mode: 'image', aspectRatio: 'wide', overlay: false },
    show: {
      favorite: false,
      logo: true,
      image: true,
      category: true,
      description: true,
      rating: true,
      reviews: true,
      address: true,
      phone: false,
      website: false,
      hours: false,
      offer: true,
      badges: true,
    },
    behavior: 'clickable',
  },
  directory: {
    media: { mode: 'image', aspectRatio: 'wide', overlay: false },
    show: {
      favorite: true,
      logo: true,
      image: true,
      category: true,
      description: true,
      rating: true,
      reviews: true,
      address: true,
      phone: true,
      website: false,
      hours: true,
      offer: true,
      badges: true,
    },
    behavior: 'clickable',
  },
  hero: {
    media: { mode: 'background', aspectRatio: 'wide', overlay: true },
    show: { phone: false, website: false },
  },
  directoryRow: {
    media: { mode: 'logo', aspectRatio: 'square' },
    show: { favorite: true, phone: true },
  },
};

const CONTEXT_DEFAULTS = {
  public: {},
  article: {
    variant: 'content',
    show: { favorite: false, phone: false, website: false, hours: false },
  },
  directory: {
    variant: 'directoryRow',
    show: { favorite: true, address: true, phone: true },
  },
  homepage: {
    variant: 'hero',
    show: { favorite: false, address: false, phone: false, website: false },
  },
  admin: {
    variant: 'compact',
    show: { favorite: false, rating: false, reviews: false, badges: true },
  },
  owner: {
    variant: 'content',
    show: { favorite: false, badges: true },
  },
  claim: {
    variant: 'content',
    show: { favorite: false, rating: false, reviews: false, badges: true },
  },
};

export function normalizeListingCardVariant(variant) {
  // Temporary aliases for pre-rename consumers. Remove after migrated usages stop passing standard/tall.
  if (variant === 'standard') return 'content';
  if (variant === 'tall') return 'directory';
  return variant;
}

const DEFAULT_MEDIA = {
  mode: 'logo',
  aspectRatio: 'square',
  overlay: false,
};

export function normalizeListing(listing = {}) {
  const address = listing.address || listing.streetAddress || [listing.addressLine1, listing.city, listing.state].filter(Boolean).join(', ');
  const name = listing.name || listing.businessName || 'Untitled listing';
  const category = listing.category || listing.categoryName || listing.primaryCategory || '';
  const imageUrl = listing.imageUrl || listing.coverImageUrl || listing.primaryImageUrl || listing.photoUrl || '';
  const logoUrl = listing.logoUrl || '';
  const initials = listing.initials || name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'LC';
  const claimStatus = listing.claimStatus || listing.ownershipStatus || listing.claim?.status || '';
  const isClaimed = Boolean(listing.isClaimed || listing.claimed || String(claimStatus).toLowerCase() === 'claimed');

  return {
    id: listing.id || '',
    slug: listing.slug || '',
    href: getListingHref(listing),
    name,
    category,
    description: listing.description || listing.shortDescription || '',
    address,
    city: listing.city || '',
    state: listing.state || '',
    phone: listing.phone || '',
    website: listing.website || listing.websiteUrl || '',
    rating: listing.rating || listing.googleRating || '',
    reviewCount: listing.reviewCount || listing.reviews || '',
    imageUrl,
    logoUrl,
    initials,
    tier: listing.tier || '',
    status: listing.status || listing.publishedStatus || '',
    claimStatus,
    isClaimed,
    badges: Array.isArray(listing.badges) ? listing.badges : [],
    offer: listing.offer || listing.offerText || null,
    hours: listing.hours || listing.businessHours || '',
    createdAt: listing.createdAt || '',
    ageLabel: listing.ageLabel || '',
  };
}

export function resolveListingCardProps({
  variant,
  context = 'public',
  tier,
  media,
  show,
  density = 'comfortable',
} = {}) {
  const contextDefaults = CONTEXT_DEFAULTS[context] || {};
  const resolvedVariant = normalizeListingCardVariant(variant || contextDefaults.variant || 'content');
  const variantDefaults = VARIANT_DEFAULTS[resolvedVariant] || {};

  return {
    variant: resolvedVariant,
    context,
    density,
    tier: tier || 'basic',
    behavior: variantDefaults.behavior || contextDefaults.behavior,
    media: {
      ...DEFAULT_MEDIA,
      ...(contextDefaults.media || {}),
      ...(variantDefaults.media || {}),
      ...(media || {}),
    },
    show: {
      ...DEFAULT_SHOW,
      ...(contextDefaults.show || {}),
      ...(variantDefaults.show || {}),
      ...(show || {}),
    },
  };
}

export function getTierClasses(tier = 'basic') {
  return {
    basic: {
      border: 'border-slate-200',
      accent: 'text-[color:var(--listing-card-text,#334155)]',
      badge: 'border-slate-200 bg-slate-50 text-slate-700',
      action: 'primary',
    },
    featured: {
      border: 'border-[color:var(--listing-card-primary,#2563EB)]',
      accent: 'text-[color:var(--listing-card-primary,#2563EB)]',
      badge: 'border-[color:var(--listing-card-primary,#2563EB)] bg-[color:var(--listing-card-primary-soft,#DBEAFE)] text-[color:var(--listing-card-primary,#2563EB)]',
      action: 'primary',
    },
    spotlight: {
      border: 'border-[color:var(--listing-card-accent,#F59E0B)]',
      accent: 'text-[color:var(--listing-card-accent,#F59E0B)]',
      badge: 'border-[color:var(--listing-card-accent,#F59E0B)] bg-[color:var(--listing-card-primary-soft,#FEF3C7)] text-[color:var(--listing-card-accent,#F59E0B)]',
      action: 'warning',
    },
  }[tier] || {
    border: 'border-slate-200',
    accent: 'text-[color:var(--listing-card-text,#334155)]',
    badge: 'border-slate-200 bg-slate-50 text-slate-700',
    action: 'primary',
  };
}

export function getActionVariant(variant = 'primary') {
  if (variant === 'secondary') return 'secondary';
  if (variant === 'ghost') return 'ghost';
  if (variant === 'danger') return 'danger';
  return 'primary';
}

export function getAspectRatioClass(aspectRatio = 'square') {
  if (aspectRatio === 'wide') return 'aspect-[16/9]';
  if (aspectRatio === 'portrait') return 'aspect-[4/5]';
  return 'aspect-square';
}

export function getListingHref(listing) {
  if (!listing) return undefined;
  const href = String(listing.href || listing.url || listing.publicPath || listing.publicUrl || '');
  if (!/[\\\r\n]/.test(href) && ((href.startsWith('/') && !href.startsWith('//')) || /^https:\/\//i.test(href))) return href;
  if (listing.slug) return publicListingPath(listing);
  return undefined;
}

export function cn(...values) {
  return clsx(...values);
}
